/**
 * @module UI.routes
 *
 * Match route patterns and redirect to appropriate section (which will handle
 * its own internal routes). Block access to authenticated sections if not
 * logged in.
 */
import React from 'react';
import { Route, Navigate, createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { SupportLogoutPage } from './Auth.section/SupportLogout.page';
import MainSection from './Main.section';
import AuthSection from './Auth.section';
import { CheckAuth } from './Main.section/Main.shared/CheckAuth';
import { ProtectedRoute } from './Main.section/Main.shared/ProtectedRoutes';
import LoginPage from './Auth.section/Login.page';
import { SSOPage } from './Auth.section/SSO.page';
import ForgotPasswordPage from './Auth.section/ForgotPassword.page';
import ResetPasswordPage from './Auth.section/ResetPassword.page';
import ActivatePage from './Auth.section/Activate.page';
import { Logout } from './Auth.section/Logout';
import { KazooWebAuthRedirect } from './Main.section/Main.shared/KazooWebAuthRedirect.tsx';
import { Restoring } from './Main.section/Main.shared/Restoring.tsx';

const createManualRoutes = () => {
  return createRoutesFromElements(
    <Route element={<Restoring />}>
      <Route path="/" element={<Navigate to="/surveys" replace />} />
      {/*Render the AuthSection if not logged in, otherwise redirect to original url*/}
      <Route
        path="/login"
        element={
          <CheckAuth>
            <KazooWebAuthRedirect>
              <AuthSection>
                <LoginPage />
              </AuthSection>
            </KazooWebAuthRedirect>
          </CheckAuth>
        }
      />
      <Route
        path="/sso"
        element={
          <CheckAuth>
            <AuthSection>
              <SSOPage />
            </AuthSection>
          </CheckAuth>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <AuthSection>
            <ForgotPasswordPage />
          </AuthSection>
        }
      />
      <Route
        path="/reset-password/:reset_token"
        element={
          <AuthSection>
            <ResetPasswordPage />
          </AuthSection>
        }
      />
      <Route
        path="/reset-password/*"
        element={
          <AuthSection>
            <ResetPasswordPage />
          </AuthSection>
        }
      />
      <Route
        path="/activate/:activation_code"
        element={
          <AuthSection>
            <ActivatePage />
          </AuthSection>
        }
      />
      <Route
        path="/activate/*"
        element={
          <AuthSection>
            <ActivatePage />
          </AuthSection>
        }
      />
      <Route
        path="/logout"
        element={
          <AuthSection>
            <Logout />
          </AuthSection>
        }
      />
      <Route
        path="/support/logout"
        element={
          <AuthSection>
            <SupportLogoutPage />
          </AuthSection>
        }
      />
      {/*Render the MainSection or redirect to /login with original url if not authenticated.*/}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainSection />
          </ProtectedRoute>
        }
      />
    </Route>,
  );
};

const router = () => {
  const manualRoutes = createManualRoutes();
  return createBrowserRouter(manualRoutes, {
    future: {
      v7_relativeSplatPath: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_fetcherPersist: true,
      v7_skipActionErrorRevalidation: true,
    },
  });
};

const UIRoutes = () => (
  <React.StrictMode>
    <RouterProvider
      future={{
        v7_startTransition: true,
      }}
      router={router()}
    />
  </React.StrictMode>
);

export default UIRoutes;
